import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MaterialTable, {
    Column,
    EditComponentProps,
} from "@material-table/core";
import * as APIModels from "../../../models";
import {
    ClientService,
    TylerAdminService,
} from "../../../services/ClientService";
import Select, { ActionMeta } from "react-select";
import {
    ErrorModal,
    ErrorModalProps,
} from "../../../components/shared/modals/ErrorModal";
import ServiceContactUpdateModal from "./modals/ServiceContactUpdateModal";
import ServiceContactAddModal from "./modals/ServiceContactAddModal";

type ServiceContact = APIModels.ServiceContact;

export default function ServiceContacts() {
    const [data, setData] = useState([] as ServiceContact[]);
    const [updateError, setUpdateError] = useState("");
    const [updateMessageError, setUpdateMessageError] = useState("");
    const [updating, setUpdating] = useState({} as ServiceContact);
    const [modal, setModal] = useState(false);
    const [updateModal, setUpdateModal] = useState(false);
    const [addModal, setAddModal] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const errorModalProps: ErrorModalProps = {
        errorMessage: "",
        onClose: () => {
            setUpdateError("");
            setModal(false);
        },
    };

    useEffect(() => {
        if (updateError !== "") {
            errorModalProps.errorMessage = updateError;
            setModal(true);
        } else {
            setModal(false);
            errorModalProps.errorMessage = "";
        }
    }, [updateError]);

    useEffect(() => {
        TylerAdminService.getServiceContacts()
            .then((res) => {
                if (res.successful && res.data!) {
                    setData((_prev) => {
                        console.log(res.data);
                        return [...res.data!];
                    });
                    setSuccess(res.data.length + " Service Contacts Loaded");
                } else if (res.successful!) {
                    const contact = {
                        firstName: "No Service",
                        lastName: "Contacts",
                        emailAddress: "contact@tylertech.com",
                    } as ServiceContact;
                    setData([contact]);
                } else {
                    setError(res.errorMessage!);
                }
            })
            .catch((err) => setError(err));
    }, []);

    const updateServiceContact = async (
        sc: ServiceContact
    ): Promise<boolean> => {
        try {
            const response = await TylerAdminService.updateServiceContact({
                body: sc,
            });
            if (response.successful) {
                setData((prev) => {
                    const index = prev.findIndex((a) => a.id === sc.id);
                    prev[index] = sc;
                    return [...prev];
                });
                setSuccess("Service Contact Updated");
                return true;
            } else {
                console.log(response.errorMessage);
                setUpdateMessageError(response.errorMessage!);
                return false;
            }
        } catch (err) {
            console.log(err);
            setUpdateMessageError(err as string);
            return false;
        }
    };

    const deleteServiceContact = async (sc: ServiceContact) => {
        try {
            const response = await TylerAdminService.deleteServiceContact({
                id: sc.id!,
            });
            if (response.successful) {
                setData((prev) => {
                    return prev.filter((att) => att.id !== sc.id);
                });
                setSuccess("Service Contact Deleted");
            } else {
                setError(response.errorMessage!);
            }
        } catch (e: unknown) {
            console.log(e);
            //setError(e.message);
        }
    };

    const addServiceContact = async (
        attorney: ServiceContact,
        addToFirm: boolean
    ): Promise<boolean> => {
        try {
            console.log(attorney);
            const response = await TylerAdminService.addServiceContact({
                body: attorney,
                addToFirm: true,
            });
            if (response.successful) {
                setData((prev) => {
                    return [...prev, attorney];
                });
                return true;
            } else {
                setUpdateError(response.errorMessage!);
                return false;
            }
        } catch (e) {
            console.log(e);
            //  setUpdateError(e.message);
            return false;
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <button
                    className="btn btn-success bt-sm float-right"
                    onClick={() => setAddModal(true)}
                >
                    <i className="fas fa-plus"></i>
                    &nbsp; Add Service Contact
                </button>
            </div>
            <div className="card-body">
                <div className="row">
                    {modal && <ErrorModal {...errorModalProps} />}
                    {updateModal && (
                        <ServiceContactUpdateModal
                            close={() => setUpdateModal(false)}
                            addServiceContactModal={updateModal}
                            updateServiceContact={updateServiceContact}
                            updating={updating}
                            message={updateMessageError}
                        />
                    )}
                    {addModal && (
                        <ServiceContactAddModal
                            close={() => setAddModal(false)}
                            addServiceContactModal={addModal}
                            addServiceContact={addServiceContact}
                        />
                    )}
                    <div className="col-12">
                        <div style={{ maxWidth: "100%" }}>
                            <MaterialTable
                                columns={[
                                    { title: "First Name", field: "firstName" },
                                    { title: "Last Name", field: "lastName" },
                                    { title: "Email", field: "emailAddress" },
                                    { title: "User Type", field: "userType" },
                                    {
                                        title: "Case Number",
                                        field: "caseNumber",
                                    },
                                    {
                                        title: "Active",
                                        field: "active",
                                        type: "boolean",
                                    },
                                ]}
                                data={data}
                                title="Firm Service Contacts"
                                options={{
                                    actionsColumnIndex: -1,
                                    pageSizeOptions: [5, 10, 20, 50, 75, 100],
                                }}
                                editable={{
                                    onRowDelete: async (oldData) => {
                                        return await deleteServiceContact(
                                            oldData
                                        );
                                    },
                                }}
                                actions={[
                                    {
                                        icon: "edit",
                                        tooltip: "Edit Service Contact",
                                        onClick: (event, rowData) => {
                                            setUpdating(
                                                rowData as ServiceContact
                                            );
                                            setUpdateModal(true);
                                        },
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {error !== "" && (
                <div className="col-12 p-2">
                    <div className="alert alert-danger">{error}</div>
                </div>
            )}
            {success !== "" && (
                <div className="col-12 p-2">
                    <div className="alert alert-success">{success}</div>
                </div>
            )}
        </div>
    );
}
